import { useState } from 'react';
import axiosInstance from '../services/axiosInstance';

export function useCollectionActions(
  onCollectionUpdated: () => void,
  showSnackbar: (message: string) => void
) {
  const [addToCollectionDialogOpen, setAddToCollectionDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [createNewCollection, setCreateNewCollection] = useState(false);
  const [addingToCollection, setAddingToCollection] = useState(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [pendingCollectionAssetIds, setPendingCollectionAssetIds] = useState<string[]>([]);

  const addAssetsToCollectionRequest = async (collectionId: string, assetIds: string[]) => {
    if (!collectionId || assetIds.length === 0) return;
    if (assetIds.length === 1) {
      await axiosInstance.post(`/api/collections/${collectionId}/assets`, {
        assetId: assetIds[0]
      });
      return;
    }
    await axiosInstance.post(`/api/collections/${collectionId}/assets/bulk`, {
      assetIds
    });
  };

  const resetDialog = () => {
    setAddToCollectionDialogOpen(false);
    setSelectedCollection(null);
    setNewCollectionName('');
    setCreateNewCollection(false);
    setCollectionError(null);
    setPendingCollectionAssetIds([]);
  };

  const handleAddToCollection = async (getAssetIds: () => string[]) => {
    const assetIdsToAdd = pendingCollectionAssetIds.length > 0
      ? pendingCollectionAssetIds
      : getAssetIds();

    if (assetIdsToAdd.length === 0) {
      setCollectionError('Ingen tillgång vald att lägga till');
      return;
    }

    setAddingToCollection(true);
    setCollectionError(null);

    try {
      if (createNewCollection && newCollectionName.trim()) {
        const response = await axiosInstance.post(`/api/collections`, {
          name: newCollectionName.trim(),
          description: '',
        });

        const createdCollectionId = response.data._id;
        if (createdCollectionId) {
          await addAssetsToCollectionRequest(createdCollectionId, assetIdsToAdd);
        } else {
          throw new Error('Kunde inte identifiera den skapade samlingen');
        }

        if (onCollectionUpdated) onCollectionUpdated();
      } else if (selectedCollection) {
        await addAssetsToCollectionRequest(selectedCollection, assetIdsToAdd);
        if (onCollectionUpdated) onCollectionUpdated();
      } else {
        setCollectionError('Välj en samling eller skapa en ny');
        setAddingToCollection(false);
        return;
      }

      resetDialog();

      const addedCount = assetIdsToAdd.length;
      showSnackbar(addedCount > 1 ? `${addedCount} tillgångar har lagts till i samlingen` : 'Tillgången har lagts till i samlingen');
    } catch (err: any) {
      console.error('Error adding asset to collection:', err);
      setCollectionError(`Error: ${err.message}`);
    } finally {
      setAddingToCollection(false);
    }
  };

  return {
    addToCollectionDialogOpen, setAddToCollectionDialogOpen,
    selectedCollection, setSelectedCollection,
    newCollectionName, setNewCollectionName,
    createNewCollection, setCreateNewCollection,
    addingToCollection,
    collectionError, setCollectionError,
    pendingCollectionAssetIds, setPendingCollectionAssetIds,
    resetDialog,
    handleAddToCollection,
  };
}
